/**
 * Parser for the tokenized print failure messages of the Star plugin (>= 7.3.2).
 *
 * The plugin prefixes its print errors with a token that tells where the failure happened:
 *  - STAR_PRE_WRITE_NOT_READY      the printer itself reported it is not ready before anything
 *                                  was sent (reason=PAPER_EMPTY, COVER_OPEN, PAPER_JAM, ...)
 *  - STAR_PRE_WRITE_NO_CONNECTION  no status could be read before writing (iOS only)
 *  - STAR_WRITE_NOTHING_SENT       the write failed before any byte was accepted
 *  - STAR_WRITE_INCOMPLETE         the write stopped part-way (written=k/n)
 *  - STAR_POST_WRITE_UNCONFIRMED   the data was sent, but the completion check failed
 *                                  (reason=IO for a link drop, or the physical reason)
 *
 * Messages without a token come from older plugin versions or from the connection phase and keep
 * their legacy handling. The token must be matched against the raw native message only, and before
 * any legacy detector: the native error text appended after `err=` may contain legacy phrases.
 */

export const STAR_PRE_WRITE_NOT_READY = 'STAR_PRE_WRITE_NOT_READY';
export const STAR_PRE_WRITE_NO_CONNECTION = 'STAR_PRE_WRITE_NO_CONNECTION';
export const STAR_WRITE_NOTHING_SENT = 'STAR_WRITE_NOTHING_SENT';
export const STAR_WRITE_INCOMPLETE = 'STAR_WRITE_INCOMPLETE';
export const STAR_POST_WRITE_UNCONFIRMED = 'STAR_POST_WRITE_UNCONFIRMED';

/**
 * Reasons that a human can confirm and fix at the printer (no paper, cover open). They are handled
 * like a lost connection: the job stays in the queue and is retried until the printer is ready.
 */
export const STAR_CONFIRMED_NOT_READY_REASONS: ReadonlyArray<string> = ['PAPER_EMPTY', 'COVER_OPEN'];

export interface StarPrintOutcome {
  token: string;
  reason?: string;
  written?: number;
  total?: number;
  /** Native error text after `err=`, if any. */
  nativeError?: string;
}

const TOKEN_PATTERN = /^(STAR_[A-Z_]+)(?=\s|$)/;
const REASON_PATTERN = /\breason=([A-Z_]+)\b/;
const WRITTEN_PATTERN = /\bwritten=(\d+)\/(\d+)\b/;
const NATIVE_ERROR_PATTERN = /\serr=(.*)$/s;

export function parseStarOutcome(rawMessage: string | undefined | null): StarPrintOutcome | undefined {
  if (!rawMessage) {
    return undefined;
  }
  const tokenMatch = TOKEN_PATTERN.exec(rawMessage);
  if (!tokenMatch) {
    return undefined;
  }
  const outcome: StarPrintOutcome = { token: tokenMatch[1] };
  const nativeErrorMatch = NATIVE_ERROR_PATTERN.exec(rawMessage);
  const head = nativeErrorMatch ? rawMessage.slice(0, nativeErrorMatch.index) : rawMessage;
  if (nativeErrorMatch) {
    outcome.nativeError = nativeErrorMatch[1];
  }
  const reasonMatch = REASON_PATTERN.exec(head);
  if (reasonMatch) {
    outcome.reason = reasonMatch[1];
  }
  const writtenMatch = WRITTEN_PATTERN.exec(head);
  if (writtenMatch) {
    outcome.written = Number(writtenMatch[1]);
    outcome.total = Number(writtenMatch[2]);
  }
  return outcome;
}

export function isStarConfirmedNotReadyReason(reason: string | undefined): reason is string {
  return !!reason && STAR_CONFIRMED_NOT_READY_REASONS.includes(reason);
}
